import { useMemo } from 'react';
import { PermissionEnum } from 'generated/graphql';
import type { User } from '~/server/types';
import { useProfessionalData } from '~/modules/professional/hook/use-professional-data';
import { getPermissions } from '../hook/use-permissions';
import { isUserLoggedIn, useUser } from '../hook/use-user';

export type ImageDownloadPageType = 'project' | 'article' | 'product';

const PAGE_VIEW_PERMISSION: Record<ImageDownloadPageType, PermissionEnum> = {
    project: PermissionEnum.BmProjectView,
    article: PermissionEnum.BmArticleView,
    product: PermissionEnum.BmProductsView,
};

function toImageDownloadPageType(
    pageType: 'professional' | 'product' | 'project' | 'article' | undefined,
): ImageDownloadPageType | undefined {
    if (
        pageType === 'project' ||
        pageType === 'article' ||
        pageType === 'product'
    ) {
        return pageType;
    }
    return undefined;
}

/**
 * Whether the user may see/use image download controls on a content detail page.
 * Primary: page-type team view permission scoped to the content professional.
 * Fallback: legacy BM_MARKETING_VIEW (same scope, includes system-pro staff merge).
 */
export function getCanDownloadImages(
    user: User | null | undefined,
    pageType: ImageDownloadPageType | null | undefined,
    contentProfessionalId?: string | number | null,
): boolean {
    if (!user || !isUserLoggedIn(user) || !pageType || !contentProfessionalId) {
        return false;
    }

    const hasPageView = getPermissions(
        user,
        PAGE_VIEW_PERMISSION[pageType],
        contentProfessionalId,
    );
    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView,
        contentProfessionalId,
    );

    return hasPageView || hasMarketingView;
}

/** Hook variant for project/article/product detail routes (via useProfessionalData). */
export function useCanDownloadImages(): boolean {
    const user = useUser();
    const professionalData = useProfessionalData();

    return useMemo(
        () =>
            getCanDownloadImages(
                user,
                toImageDownloadPageType(professionalData?.pageType),
                professionalData?.professional?.ID,
            ),
        [user, professionalData],
    );
}
